import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Grid, TextField, MenuItem, Button, Autocomplete, ToggleButtonGroup, ToggleButton,
  CircularProgress, InputAdornment, Stack,
} from '@mui/material';
import { SaveOutlined } from '@mui/icons-material';
import {
  P, PX, cardSx, fieldSx, priBtn, outBtn, FieldLabel, WorkspaceHeader, ValidationStrip, StatusPill,
} from '../../components/common/workspaceKit';
import { priceListService, PriceListInput } from '../../services/priceListService';
import { productService, Product } from '../../services/productService';
import { customerService, Customer } from '../../services/customerService';

const todayISO = () => new Date().toISOString().slice(0, 10);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: PX, mb: 2.5 }}>
    <Box sx={{ fontSize: '13px', fontWeight: 800, color: P.strong, mb: 2 }}>{title}</Box>
    {children}
  </Paper>
);

const PriceListFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [form, setForm] = useState<PriceListInput & { productId: number | null; customerId: number | null }>({
    listName: '', priceType: 'CUSTOMER', productId: null, customerId: null,
    effectiveFrom: todayISO(), effectiveTo: '', minQty: 0, uom: '', specialPrice: 0,
    priority: 0, currencyCode: 'INR', isActive: true,
  } as any);

  useEffect(() => {
    productService.getProducts({ limit: 500 } as any).then((r) => setProducts(r.products || [])).catch(() => {});
    customerService.getCustomers().then(setCustomers).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    priceListService.get(id!)
      .then((r) => {
        const p = r.priceList;
        setForm({
          listName: p.list_name || '', priceType: p.price_type, productId: p.product_id,
          customerId: p.customer_id, effectiveFrom: String(p.effective_from).slice(0, 10),
          effectiveTo: p.effective_to ? String(p.effective_to).slice(0, 10) : '',
          minQty: Number(p.min_qty || 0), uom: p.uom || '', specialPrice: Number(p.special_price || 0),
          priority: p.priority || 0, currencyCode: p.currency_code || 'INR', isActive: !!p.is_active,
        } as any);
      })
      .catch((e) => setError(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const validations = useMemo(() => [
    { ok: !!form.productId, label: 'Product selected' },
    { ok: form.priceType === 'GENERAL' || !!form.customerId, label: 'Customer selected' },
    { ok: !!form.effectiveFrom, label: 'Effective-from set' },
    { ok: Number(form.specialPrice) > 0, label: 'Rate > 0' },
  ], [form]);

  const canSave = validations.every((v) => v.ok);

  const submit = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    const payload: PriceListInput = {
      listName: form.listName || undefined,
      priceType: form.priceType,
      productId: form.productId as number,
      customerId: form.priceType === 'CUSTOMER' ? form.customerId : null,
      effectiveFrom: form.effectiveFrom,
      effectiveTo: form.effectiveTo || null,
      minQty: Number(form.minQty) || 0,
      uom: form.uom || undefined,
      specialPrice: Number(form.specialPrice),
      priority: Number(form.priority) || 0,
      currencyCode: form.currencyCode || 'INR',
      isActive: form.isActive,
    };
    try {
      if (isEdit) await priceListService.update(id!, payload);
      else await priceListService.create(payload);
      navigate('/sales/price-lists');
    } catch (e: any) {
      setError(e?.message || 'Save failed');
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%' }}>
      <WorkspaceHeader
        onBack={() => navigate('/sales/price-lists')}
        title={isEdit ? 'Edit Price List' : 'New Price List'}
        subtitle="Rate master entry — resolved onto quotation / sales-order lines by customer, product, date and quantity."
        chip={<StatusPill label={form.priceType === 'CUSTOMER' ? 'Customer' : 'General'} tone={form.priceType === 'CUSTOMER' ? 'primary' : 'muted'} />}
        actions={<Button variant="contained" startIcon={<SaveOutlined />} sx={priBtn} disabled={!canSave || saving} onClick={submit}>{saving ? 'Saving…' : 'Save'}</Button>}
      />

      <ValidationStrip items={validations} />

      <Box sx={{ px: PX, pb: 4, maxWidth: 900 }}>
        {error && <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2.5, bgcolor: P.redBg, color: P.red, fontWeight: 600, fontSize: 13 }}>{error}</Paper>}

        <Section title="Scope">
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel text="List name" />
              <TextField fullWidth size="small" sx={fieldSx} value={form.listName} onChange={(e) => set('listName', e.target.value)} placeholder="e.g. Q3 Wholesale — North" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel text="Price type" required />
              <ToggleButtonGroup exclusive size="small" value={form.priceType} onChange={(_, v) => v && set('priceType', v)} sx={{ height: 42 }}>
                <ToggleButton value="CUSTOMER" sx={{ textTransform: 'none', px: 2 }}>Customer-specific</ToggleButton>
                <ToggleButton value="GENERAL" sx={{ textTransform: 'none', px: 2 }}>General</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FieldLabel text="Product" required />
              <Autocomplete
                size="small"
                options={products}
                getOptionLabel={(o) => `${o.product_code} — ${o.product_name}`}
                value={products.find((p) => Number(p.id) === Number(form.productId)) || null}
                onChange={(_, v) => set('productId', v ? Number(v.id) : null)}
                renderInput={(params) => <TextField {...params} sx={fieldSx} placeholder="Select product" />}
              />
            </Grid>
            {form.priceType === 'CUSTOMER' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldLabel text="Customer" required />
                <Autocomplete
                  size="small"
                  options={customers}
                  getOptionLabel={(o) => `${o.customer_code} — ${o.company_name}`}
                  value={customers.find((c) => Number(c.id) === Number(form.customerId)) || null}
                  onChange={(_, v) => set('customerId', v ? Number(v.id) : null)}
                  renderInput={(params) => <TextField {...params} sx={fieldSx} placeholder="Select customer" />}
                />
              </Grid>
            )}
          </Grid>
        </Section>

        <Section title="Rate & validity">
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Special price" required />
              <TextField fullWidth size="small" type="number" sx={fieldSx} value={form.specialPrice}
                onChange={(e) => set('specialPrice', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Min qty (break)" />
              <TextField fullWidth size="small" type="number" sx={fieldSx} value={form.minQty} onChange={(e) => set('minQty', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="UOM" />
              <TextField fullWidth size="small" sx={fieldSx} value={form.uom} onChange={(e) => set('uom', e.target.value)} placeholder="Nos / Kg" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Effective from" required />
              <TextField fullWidth size="small" type="date" sx={fieldSx} InputLabelProps={{ shrink: true }} value={form.effectiveFrom} onChange={(e) => set('effectiveFrom', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Effective to" />
              <TextField fullWidth size="small" type="date" sx={fieldSx} InputLabelProps={{ shrink: true }} value={form.effectiveTo} onChange={(e) => set('effectiveTo', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Priority" />
              <TextField fullWidth size="small" type="number" sx={fieldSx} value={form.priority} onChange={(e) => set('priority', e.target.value)} helperText="Higher wins on a tie" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Currency" />
              <TextField fullWidth size="small" select sx={fieldSx} value={form.currencyCode} onChange={(e) => set('currencyCode', e.target.value)}>
                {['INR', 'USD', 'EUR', 'GBP'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel text="Status" />
              <TextField fullWidth size="small" select sx={fieldSx} value={form.isActive ? '1' : '0'} onChange={(e) => set('isActive', e.target.value === '1')}>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Section>

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button variant="outlined" sx={outBtn} onClick={() => navigate('/sales/price-lists')}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveOutlined />} sx={priBtn} disabled={!canSave || saving} onClick={submit}>{saving ? 'Saving…' : 'Save Price List'}</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default PriceListFormPage;
